import React, { useState, useEffect, useContext } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import CssBaseline from '@material-ui/core/CssBaseline';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import GenericDialogBox from '../../components/GenericDialogBox';
import InfoBox from '../../components/InfoBox';
import DataService from "../../services/data.service";
import { useTranslation } from "react-i18next";
import PermissionContext from '../../contexts/PermissionContext';

const validationSchema = yup.object().shape({
    name: yup
        .string('Enter profesional name')
        .required('Name is required')
        .min(4, 'Names should be of minimum 4 characters length'),
    phone: yup
        .string('Enter profesional phone')
        .required('Phone is required')
        .min(8, 'Phone should be of minimum 8 characters length'),
    comision: yup
        .number('Enter comision to pay')
        .nullable(),
    salary: yup
        .number('Enter salary')
        .required('salary is required'),
    work_hours: yup
        .number('Enter work hours')
        .required('Work hours is required'),
    pr_type_id: yup
        .number('Select a payrroll type'),
    email: yup
        .string('Enter your email')
        .email('Enter a valid email')
        .nullable(),
    gender: yup
        .string('Only strings allowed')
        .nullable(),
    dni: yup
        .string('Enter your dni')
        .nullable(),
    cuil: yup
        .string('Enter your cuil')
        .nullable(),
    address: yup
        .string('Address')
        .min(10, 'Address should be of minimum 10 characters length')
        .nullable(),
    state: yup
        .string('State')
        .min(5, 'State should be of minimum 5 characters length')
        .nullable(),
    city: yup
        .string('City')
        .min(5, 'City should be of minimum 5 characters length')
        .nullable(),
    zip: yup
        .string('Zip Code')
        .min(4, 'Zip Code should be of minimum 4 characters length')
        .nullable()
});


const useStyles = makeStyles((theme) => ({
    paper: {
        marginTop: theme.spacing(18),
        maxWidth: 800,
        margin: 'auto',
        overflow: 'hidden',
        backgroundColor: 'white',
        [theme.breakpoints.down("sm")]: {
            maxWidth: 420,
            marginLeft: 0,
        }
    },
    avatar: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
    },
    form: {

        margin: theme.spacing(3, 2, 2),
    },
    submit: {
        margin: theme.spacing(3, 0, 2),
    },
}));


const prTypeEndpoint = `pr-type`
const endpoint = `profesional`


const ProfesionalForm = React.forwardRef((props, ref) => {
    const { t } = useTranslation();

    const { idToUpdate, permission } = props;

    const { userHasPermission } = useContext(PermissionContext)



    let hasPerm = userHasPermission(permission, 'can_list')
    let hasAddPerm = userHasPermission(permission, 'can_post')
    let hasEditPerm = userHasPermission(permission, 'can_patch')
    let hasGetPerm = userHasPermission(permission, 'can_get')
    let hasDeletePerm = userHasPermission(permission, 'can_delete')
    // for detail form
    let hasDeleteDetailPerm = userHasPermission(`${permission}-detail`, 'can_delete')

    //Pr type data

    const [prtypeData, setPRTypeData] = useState([]);
    useEffect(() => {

        retrievePRTypeDataData()
    }, []); // Those ARE connectec

    const retrievePRTypeDataData = () => {
        DataService.getAll(prTypeEndpoint)
            .then(response => {
                setPRTypeData(response.data)
                // console.log(response.data);
            })
            .catch(e => {
                openNoticeBox("Error", `Code: ${e.response.status} Message: ${e.response.statusText}`)
            });
    }

    const [prtypeValue, setPRTypeValue] = React.useState(prtypeData[0]);
    const [prtypeInputValue, setPRTypeInputValue] = React.useState('');

    // INFO NOTIFICATION VARS
    const [infoOpen, setInfoOpen] = useState(false);
    const [infoTitle, setInfoTitle] = useState('');
    const [infoBody, setInfoBody] = useState('');
    //***********************

    //Dialog Vars
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogueTitle, setDialogueTitle] = useState('');
    const [dialogueBody, setDialogueBody] = useState('');
    //**********

    // DIALOG FUNCTIONS
    const handleDialogOk = () => {
        setDialogOpen(false)
    };

    const handleDialogClose = () => {
        setDialogOpen(false)
    };

    const openDialogBox = (title, body) => {
        setDialogueTitle(title)
        setDialogueBody(body)
        setDialogOpen(true)
    };
    //*******************

    // Notification INFO functions
    const openNoticeBox = (title, body) => {
        setInfoTitle(title)
        setInfoBody(body)
        setInfoOpen(true)
    };

    const handleInfoClose = () => {
        setInfoOpen(false)
    };
    // *****************

    const emptyData = {
        'name': '',
        'phone': '',
        'comision': 0.00,
        'salary': 0.00,
        'work_hours': 0.00,
        'pr_type_id': '',
        'email': '',
        'gender': '',
        'cuil': '',
        'dni': '',
        'address': '',
        'state': '',
        'city': '',
        'zip': '',
    }

    const [data, setData] = useState(emptyData);
    useEffect(() => {
        let id = typeof idToUpdate === 'object' ? 0 : idToUpdate
        getData(id)
    }, []);

    const getData = (id) => {
        DataService.get(id, endpoint)
            .then(response => {
                // console.log("data get",response.data)
                setData(response.data)
                setPRTypeValue(response.data.pr_type)

            })
            .catch(e => {
                if (e.response.status != '404') {
                    openNoticeBox("Error", `Code: ${e.response.status} Message: ${e.response.statusText}`)
                    // setData(emptyData)
                } else {
                    setData(emptyData)
                }

            });
    }

    const updateData = (values) => {
        values.pr_type_id = prtypeValue.id
        DataService.update(endpoint, values)
            .then(response => {
                openNoticeBox("Notice", t("record_updated_successfully", { "table": t("profesional_table_title") }))
                setData(emptyData)
            })
            .catch(e => {
                openNoticeBox("Error", `Code: ${e.response.status} Message: ${e.response.statusText}`)
            });
    }

    const addData = (values) => {
        values.pr_type_id = prtypeValue.id
        console.log("adding", values)
        DataService.create(endpoint, values)
            .then(response => {
                openNoticeBox("Notice", t("record_created_successfully", { "table": t("profesional_table_title") }))
                setData(emptyData)
            })
            .catch(e => {
                openNoticeBox("Error", `Code: ${e.response.status} Message: ${e.response.statusText}`)
            });
    }

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: data,
        validationSchema: validationSchema,
        onReset: () => {
        },
        onSubmit: (values, { setSubmitting, setErrors, setStatus, resetForm }) => {
            console.log(values)
            if (typeof idToUpdate === 'object') {
                addData(values)
                resetForm()
            }
            if (typeof idToUpdate === 'number') {
                // console.log("submit update",values)
                values.id = idToUpdate
                updateData(values)
            }

        },
    });
    const classes = useStyles();
    return (
        <Paper className={classes.paper}>
            <CssBaseline />
            <GenericDialogBox
                opn={dialogOpen}
                handleDialogOk={handleDialogOk}
                handleDialogClose={handleDialogClose}
                title={dialogueTitle}
                body={dialogueBody} />
            <InfoBox
                opn={infoOpen}
                handleDialogClose={handleInfoClose}
                title={infoTitle}
                body={infoBody} />

            <form className={classes.form} onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
                <Grid container spacing={1}>
                    <Grid item md={4} xs={12}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="name"
                            label={t("profesional_form_lbl_profesional_name")}
                            name="name"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            error={formik.touched.name && Boolean(formik.errors.name)}
                            helperText={formik.touched.name && formik.errors.name}
                        />
                    </Grid>
                    <Grid item md={4} xs={12}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="phone"
                            label={t("profesional_form_lbl_profesional_phone")}
                            name="phone"
                            value={formik.values.phone}
                            onChange={formik.handleChange}
                            error={formik.touched.phone && Boolean(formik.errors.phone)}
                            helperText={formik.touched.phone && formik.errors.phone}
                        />
                    </Grid>
                    <Grid item md={4} xs={12}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="email"
                            label={t("profesional_form_lbl_profesional_email")}
                            name="email"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            error={formik.touched.email && Boolean(formik.errors.email)}
                            helperText={formik.touched.email && formik.errors.email}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete
                            id="pr_type_id"
                            name="pr_type_id"
                            // value={value}
                            options={prtypeData}
                            getOptionLabel={(option) => option.name ? option.name : "-"}
                            onChange={(event, newValue) => {
                                console.log("newValue", newValue)
                                setPRTypeValue(newValue);
                            }}
                            inputValue={prtypeInputValue}
                            onInputChange={(event, newInputValue) => {
                                console.log("change", newInputValue)
                                setPRTypeInputValue(newInputValue);
                            }}

                            value={prtypeValue ? prtypeValue : ""}
                            renderInput={(params) => <TextField {...params} label={t("product_form_lbl_prtype")} variant="outlined" />}
                            getOptionSelected={(option, value) => option.value === value.value}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="comision"
                            label={t("profesional_form_lbl_profesional_comision")}
                            name="comision"
                            value={formik.values.comision}
                            onChange={formik.handleChange}
                            error={formik.touched.comision && Boolean(formik.errors.comision)}
                            helperText={formik.touched.comision && formik.errors.comision}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="work_hours"
                            label={t("profesional_form_lbl_profesional_work_hours")}
                            name="work_hours"
                            value={formik.values.work_hours}
                            onChange={formik.handleChange}
                            error={formik.touched.work_hours && Boolean(formik.errors.work_hours)}
                            helperText={formik.touched.work_hours && formik.errors.work_hours}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="salary"
                            label={t("profesional_form_lbl_profesional_salary")}
                            name="salary"
                            value={formik.values.salary}
                            onChange={formik.handleChange}
                            error={formik.touched.salary && Boolean(formik.errors.salary)}
                            helperText={formik.touched.salary && formik.errors.salary}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="gender"
                            label={t("profesional_form_lbl_profesional_gender")}
                            name="gender"
                            value={formik.values.gender}
                            onChange={formik.handleChange}
                            error={formik.touched.gender && Boolean(formik.errors.gender)}
                            helperText={formik.touched.gender && formik.errors.gender}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="dni"
                            label={t("profesional_form_lbl_profesional_dni")}
                            name="dni"
                            value={formik.values.dni}
                            onChange={formik.handleChange}
                            error={formik.touched.dni && Boolean(formik.errors.dni)}
                            helperText={formik.touched.dni && formik.errors.dni}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="cuil"
                            label={t("profesional_form_lbl_profesional_cuil")}
                            name="cuil"
                            value={formik.values.cuil}
                            onChange={formik.handleChange}
                            error={formik.touched.cuil && Boolean(formik.errors.cuil)}
                            helperText={formik.touched.cuil && formik.errors.cuil}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="address"
                            label={t("profesional_form_lbl_profesional_address")}
                            name="address"
                            value={formik.values.address}
                            onChange={formik.handleChange}
                            error={formik.touched.address && Boolean(formik.errors.address)}
                            helperText={formik.touched.address && formik.errors.address}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="state"
                            label={t("profesional_form_lbl_profesional_state")}
                            name="state"
                            value={formik.values.state}
                            onChange={formik.handleChange}
                            error={formik.touched.state && Boolean(formik.errors.state)}
                            helperText={formik.touched.state && formik.errors.state}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="city"
                            label={t("profesional_form_lbl_profesional_city")}
                            name="city"
                            value={formik.values.city}
                            onChange={formik.handleChange}
                            error={formik.touched.city && Boolean(formik.errors.city)}
                            helperText={formik.touched.city && formik.errors.city}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="zip"
                            label={t("profesional_form_lbl_profesional_zip")}
                            name="zip"
                            value={formik.values.zip}
                            onChange={formik.handleChange}
                            error={formik.touched.zip && Boolean(formik.errors.zip)}
                            helperText={formik.touched.zip && formik.errors.zip}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            className={classes.submit}
                            disabled={typeof idToUpdate === 'object' && hasAddPerm ? false : hasEditPerm ? false : true}

                        >
                            {typeof idToUpdate === 'object' ? t("save_form_button") : t("update_form_button")}
                        </Button>
                    </Grid>

                </Grid>
            </form>

            <Box mt={8}>

            </Box>
        </Paper>
    )
})

export default ProfesionalForm;
    // ReactDOM.render(<WithMaterialUI />, document.getElementById('root'));
